// Banner3D.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import categoriesData from '../components/categoriesData';
import '../styles/components/Banner3D.scss';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebasedeconfig';
import notificationService from '../services/notificationService';
import { useLanguage } from '../contexts/LanguageContext';
import UserXpAvatar from "../components/UserXpAvatar";
import { defaultAvatarUrl } from "../constants";
import {
  getDisplayActivityScore,
  getLevelTitle,
} from "../components/levelUtils";

const Banner3D = ({ topics }) => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const quantity = topics.length;
  const [previousTopics, setPreviousTopics] = useState([]);

  // Fonction pour extraire la première image du contenu
  const extractFirstImage = (content) => {
    if (!content) return null;
    const regex = /<img[^>]+src="([^">]+)"/i;
    const match = regex.exec(content);
    return match ? match[1] : null;
  };

  const normalizeText = (value = "") => {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  const getTmdbImageUrl = (path, size = "w780") => {
    if (!path) return null;

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const safePath = path.startsWith("/") ? path : `/${path}`;
    return `https://image.tmdb.org/t/p/${size}${safePath}`;
  };

  // Effet pour comparer les sujets précédents avec les nouveaux et générer des notifications
  useEffect(() => {
    const checkHotTopics = async () => {
      // Si c'est le premier rendu, sauvegarder simplement les sujets et quitter
      if (previousTopics.length === 0) {
        setPreviousTopics(topics);
        return;
      }

      // Créer des Maps pour faciliter la comparaison
      const previousTopicsMap = new Map(previousTopics.map(topic => [topic.id, topic]));
      const currentTopicsMap = new Map(topics.map(topic => [topic.id, topic]));

      // 1. Vérifier les sujets qui sont devenus "en feu" (nouveaux dans le top)
      const newHotTopics = topics.filter(topic => !previousTopicsMap.has(topic.id));

      // 2. Vérifier les sujets qui ne sont plus "en feu" (ne sont plus dans le top)
      const declassedTopics = previousTopics.filter(topic => !currentTopicsMap.has(topic.id));

      // Créer les notifications pour les nouveaux sujets "en feu"
      for (const topic of newHotTopics) {
        try {
          // Ne notifier que si l'auteur existe
          if (topic.authorId) {
            // Vérifier si l'utilisateur a déjà été notifié pour ce sujet
            const alreadyNotified = await checkIfAlreadyNotified(topic.authorId, topic.id, 'forum_hot');

            if (!alreadyNotified) {
              await notificationService.createHotTopicNotification(
                topic.authorId,
                topic.author || 'Auteur inconnu',
                topic.id,
                topic.title
              );
              console.log(`Notification créée pour le sujet en feu: ${topic.title}`);
            }
          }
        } catch (error) {
          console.error("Erreur lors de la création de notification pour sujet en feu:", error);
        }
      }

      // Créer les notifications pour les sujets déclassés
      for (const topic of declassedTopics) {
        try {
          // Ne notifier que si l'auteur existe
          if (topic.authorId) {
            // Vérifier si l'utilisateur a déjà été notifié pour ce sujet
            const alreadyNotified = await checkIfAlreadyNotified(topic.authorId, topic.id, 'forum_cold');

            if (!alreadyNotified) {
              await notificationService.createColdTopicNotification(
                topic.authorId,
                topic.author || 'Auteur inconnu',
                topic.id,
                topic.title
              );
              console.log(`Notification créée pour le sujet déclassé: ${topic.title}`);
            }
          }
        } catch (error) {
          console.error("Erreur lors de la création de notification pour sujet déclassé:", error);
        }
      }

      // Mettre à jour la liste des sujets précédents
      setPreviousTopics(topics);
    };

    checkHotTopics();
  }, [topics]);

  // Fonction pour vérifier si une notification a déjà été envoyée
  const checkIfAlreadyNotified = async (userId, subjectId, notificationType) => {
    try {
      const notificationsRef = collection(db, 'users', userId, 'notifications');
      const q = query(
        notificationsRef,
        where('type', '==', notificationType),
        where('subjectId', '==', subjectId)
      );

      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Erreur lors de la vérification des notifications existantes:", error);
      return false; // En cas d'erreur, on suppose qu'il n'y a pas de notification
    }
  };

  return (
    <div className="banner">
      {/* Slider 3D */}
      <div className="slider" style={{ '--quantity': quantity }}>
        {topics.map((topic, index) => {
          const userImage = extractFirstImage(topic.content);
          const normalizedTopicCategory = normalizeText(topic.category);

          const isMovieReviewTopic =
            topic.source === "movie_rating_review" ||
            topic.type === "movie_rating_discussion" ||
            Boolean(topic.movieRatingKey);

          const movieBackdropImage = getTmdbImageUrl(topic.movieBackdropPath, "w780");
          const moviePosterImage = getTmdbImageUrl(topic.moviePosterPath, "w500");

          const categoryInfo = categoriesData.find((cat) => {
            return (
              normalizeText(cat.id) === normalizedTopicCategory ||
              normalizeText(cat.name) === normalizedTopicCategory
            );
          });

          const backgroundImage = isMovieReviewTopic
            ? movieBackdropImage || moviePosterImage || userImage || categoryInfo?.image || "images/default.jpg"
            : userImage || categoryInfo?.image || movieBackdropImage || moviePosterImage || "images/default.jpg";

          const displayedAuthorName =
            topic.authorName || topic.author || "Auteur inconnu";

          const authorActivityScore =
            topic.activityScore ?? topic.messageCount ?? 0;

          const fallbackAuthorActivityScore =
            topic.messageCount ?? 0;

          const displayedAuthorActivityScore =
            getDisplayActivityScore(authorActivityScore, topic.authorId);

          const authorLevelKey =
            getLevelTitle(displayedAuthorActivityScore);

          const authorLevelTitle =
            t(`forum.levelTitles.${authorLevelKey}`);

          return (
            <div
              key={topic.id}
              className={`item${isMovieReviewTopic ? " item--movie-review" : ""}`}
              style={{
                '--position': index + 1,
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/subject/${topic.id}`)}
            >
              {/* Gradient animé en fond */}
              <div className="gradient-border"></div>

              {/* Contenu (image + overlay) */}
              <div
                className="inner"
                style={{
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div
                  className="card-overlay"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    padding: '0.5rem',
                  }}
                >
                  <div
                    className="card-contentTitle"
                    style={{
                      color: '#D90093',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      textAlign: 'center',
                      borderRadius: '5px',
                    }}
                  >
                    <h3>{topic.title}</h3>
                  </div>
                  <p>
                    Catégorie :{' '}
                    <span style={{ color: '#00FFFF', fontWeight: 'bold' }}>
                      {t(`forum.forumCategories.${topic.category}`)}
                    </span>
                  </p>
                  <div className="banner3d-author-line">
                    <span className="banner3d-author-line__label">
                      Publié par :
                    </span>

                    <div className="banner3d-author-line__identity">
                      <UserXpAvatar
                        avatarUrl={topic.avatar || defaultAvatarUrl}
                        activityScore={authorActivityScore}
                        fallbackActivityScore={fallbackAuthorActivityScore}
                        userId={topic.authorId}
                        showTitle={false}
                        showActivity={false}
                        className="user-xp-avatar--banner3d"
                      />

                      <div className="banner3d-author-line__text">
                        <span className="banner3d-author-line__name">
                          {displayedAuthorName}
                        </span>

                        <span className="banner3d-author-line__level">
                          {authorLevelTitle}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p>
                    Date :{' '}
                    <span style={{ color: '#00FFFF', fontWeight: 'bold' }}>
                      {topic.timestamp
                        ? new Date(topic.timestamp.seconds * 1000).toLocaleString()
                        : 'Date inconnue'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Section texte en bas */}
      <div className="content">
        <h3 className={`fireText firetext-${currentLanguage}`}>
          {t('banner3d.hotTopics').split('\n').map((line, index, arr) => (
            <React.Fragment key={index}>
              {line}
              {index < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h3>
        <div className="author" style={{ marginLeft: '11.5rem' }}>
          <h2>{t('banner3d.author')}</h2>
          <p>
            <b>{t('banner3d.discussionForum')}</b>
          </p>
          <p className='fireTextSecondPara'>
            {t('banner3d.shareAndDiscover')}
          </p>
        </div>
        <div className="model"></div>
      </div>
    </div>
  );
};

export default Banner3D;



